import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listMatches, deleteMatch } from "@/lib/api";
import { Trash2, Eye, Activity } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function History() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "scorer";
  const canScore = user?.role === "admin" || user?.role === "scorer";

  const refresh = () => listMatches().then(setMatches).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const onDelete = async (id) => {
    if (!confirm("Delete this match? This cannot be undone.")) return;
    try {
      await deleteMatch(id);
      toast.success("Match deleted");
      refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="history-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Archive</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-8">
        Match History
      </h1>

      {loading ? (
        <p className="text-muted-foreground uppercase tracking-[0.3em] text-xs">Loading...</p>
      ) : matches.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground uppercase text-xs tracking-[0.2em] mb-4">No matches yet</p>
          {canScore ? (
            <Link to="/new" className="tactical-btn bg-primary text-primary-foreground px-6 py-3 inline-flex items-center text-sm">Create one</Link>
          ) : (
            <Link to="/login" className="tactical-btn border border-border hover:border-primary px-6 py-3 inline-flex items-center text-sm">Sign in to score</Link>
          )}
        </div>
      ) : (
        <div className="border border-border bg-card divide-y divide-border">
          {matches.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#0F1410] transition-colors" data-testid={`history-row-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold mb-1">
                  <span>{m.format}</span><span>·</span><span>{m.overs} ov</span>
                  {m.status === "live" && <span className="flex items-center gap-1 text-destructive"><span className="pulse-dot"></span>Live</span>}
                </div>
                <p className="font-display uppercase font-black text-lg tracking-tight truncate">
                  {m.teams[0].name} <span className="text-muted-foreground">vs</span> {m.teams[1].name}
                </p>
                <div className="font-mono text-xs text-muted-foreground mt-1 stat-tabular">
                  {m.innings_summary[0] ? `${m.innings_summary[0].runs}/${m.innings_summary[0].wickets} (${m.innings_summary[0].overs})` : "—"}
                  {m.innings_summary[1] ? <> · {m.innings_summary[1].runs}/{m.innings_summary[1].wickets} ({m.innings_summary[1].overs})</> : null}
                </div>
                {m.result && <p className="text-xs text-primary font-bold mt-1">{m.result}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {m.status === "live" ? (
                  canScore ? (
                    <Link to={`/match/${m.id}/score`} className="tactical-btn bg-primary text-primary-foreground px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid={`continue-${m.id}`}>
                      <Activity className="w-3 h-3" strokeWidth={3} /> Continue
                    </Link>
                  ) : (
                    <Link to={`/watch/${m.id}`} className="tactical-btn bg-primary text-primary-foreground px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid={`watch-${m.id}`}>
                      <Eye className="w-3 h-3" strokeWidth={3} /> Watch Live
                    </Link>
                  )
                ) : (
                  <Link to={`/match/${m.id}`} className="tactical-btn border border-border hover:border-primary px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid={`view-${m.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </Link>
                )}
                {canDelete && (
                  <button
                    onClick={() => onDelete(m.id)}
                    data-testid={`delete-${m.id}`}
                    className="p-2 text-muted-foreground hover:text-destructive border border-transparent hover:border-destructive transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
